import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FaNodeJs, FaDatabase, FaDocker, FaPython, FaRobot, FaGithub, FaReact, } from "react-icons/fa";
import { SiSocketdotio, SiMongodb } from "react-icons/si";
import "../styles/projects.css";

const projectItems = [
  {
    title: "Quotes Api",
    period: "Project Overview",
    description:
      "Quote-Management-API mit Node.js und Express, das CRUD-Operationen unterstützt, mit einem Master-Key für Massenlöschungen gesichert ist und eine einfache UI mit React integriert wurde.",
    longDescription: "A comprehensive quote management system featuring secure CRUD operations, master key authentication for bulk operations, and a modern React-based user interface. The API is built with Node.js and Express, implementing best practices for security and performance.",
    icons: [
      <FaNodeJs className="tech-icon" key="nodejs" />,
      <FaReact className="tech-icon" key="react" />,
    ],
    githubUrl: "https://github.com/SpAwNnScode/quotes-api",
    status: "Completed",
    category: "Full Stack",
    technologies: ["Node.js", "Express", "React"],
    features: [
      "CRUD Operations",
      "Master Key Authentication",
      "React UI Integration",
      "API Documentation"
    ]
  },
  {
    title: "Todoo App",
    period: "Project Overview",
    description:
      "To-Do app with React, Node.js, Express, and PostgreSQL, featuring separate frontend and backend services, orchestrated with Docker Compose for streamlined development and deployment.",
    longDescription: "A modern task management application built with a microservices architecture. Features include real-time updates, task categorization, priority management, and a responsive design that works seamlessly across all devices.",
    icons: [
      <FaNodeJs className="tech-icon" key="nodejs" />,
      <FaDatabase className="tech-icon" key="postgres" />,
      <FaDocker className="tech-icon" key="docker" />
    ],
    githubUrl: "https://github.com/SpAwNnScode/todoo-app",
    status: "Completed",
    category: "Full Stack",
    technologies: ["React", "Node.js", "PostgreSQL", "Docker", "Express"],
    features: [
      "Real-time Updates",
      "Task Categories",
      "Priority Management",
      "Docker Deployment"
    ]
  },
  {
    title: "Real-Time Chat Application",
    period: "Project Overview",
    description:
      "A real-time chat application built with React, Socket.io, and Express. Users can join rooms, chat in real-time, and see active participants with a responsive UI.",
    longDescription: "An interactive chat platform that enables real-time communication between users. Features include room-based chat, user presence indicators, message history, and a modern, responsive interface.",
    icons: [
      <FaNodeJs className="tech-icon" key="nodejs" />,
      <FaReact className="tech-icon" key="react" />,
      <SiSocketdotio className="tech-icon" key="socketio" />,
      <SiMongodb className="tech-icon" key="mongodb" />
    ],
    githubUrl: "https://github.com/SpAwNnScode/chatApp",
    status: "Completed",
    category: "Full Stack",
    technologies: ["React", "Socket.io", "Node.js", "Express", "MongoDB"],
    features: [
      "Real-time Messaging",
      "Room Management",
      "User Presence",
      "Message History"
    ]
  },
  {
    title: "AI Chatbot vs Rule-Based Chatbot",
    period: "Bachelor Thesis Project",
    description:
      "Conducted an in-depth comparative study evaluating an AI-powered chatbot—integrating the SAIA AI Accelerator platform—against a traditional rule-based system, implemented using Python and Flask.",
    longDescription: "A comprehensive research project comparing the effectiveness of AI-powered and rule-based chatbots. The study includes detailed analysis of response accuracy, user satisfaction, and implementation complexity across both approaches.",
    icons: [
      <FaPython className="tech-icon" key="python" />,
      <FaRobot className="tech-icon" key="robot" />
    ],
    githubUrl: "https://github.com/SpAwNnScode/BachloerArbeit",
    status: "Completed",
    category: "AI/ML",
    technologies: ["Python", "Flask", "OpenAI API", "NLP"],
    features: [
      "AI Integration",
      "Rule-based System",
      "Comparative Analysis",
      "User Testing"
    ]
  }
];

const Projects = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [expandedProject, setExpandedProject] = useState(null);

  const categories = ["All", ...new Set(projectItems.map(item => item.category))];

  const filteredProjects = selectedCategory === "All" 
    ? projectItems 
    : projectItems.filter(item => item.category === selectedCategory);

  return (
    <section className="projects-section" id="projects">
      <h2 className="projects-header">Projects</h2>
      
      <div className="category-filter">
        {categories.map(category => (
          <button
            key={category}
            className={`category-btn ${selectedCategory === category ? 'active' : ''}`}
            onClick={() => setSelectedCategory(category)}
          >
            {category}
          </button>
        ))}
      </div>

      <div className="project-grid">
        <AnimatePresence>
          {filteredProjects.map((item, index) => (
            <motion.div
              key={index}
              className="project-card"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              onClick={() => setExpandedProject(expandedProject === index ? null : index)}
            >
              <div className="card-header">
                <h3 className="card-title">
                  {item.title}
                </h3>
                <div className="card-icons">
                  {item.icons}
                </div>
              </div>
              <div className="card-actions">
                <a
                  href={item.githubUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="action-link"
                  onClick={(e) => e.stopPropagation()}
                >
                  <FaGithub className="action-icon" /> View Code
                </a>
              </div>
              
              <div className="card-content">
                <div className="card-meta">
                  <span className="project-period">{item.period}</span>
                  <span className={`project-status ${item.status.toLowerCase()}`}>
                    {item.status}
                  </span>
                </div>
                
                <p className="project-description">{item.description}</p>
                
                <div className="tech-stack">
                  {item.technologies.map((tech, i) => (
                    <span key={i} className="tech-badge">{tech}</span>
                  ))}
                </div>

                <AnimatePresence>
                  {expandedProject === index && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="expanded-content"
                    >
                      <p className="long-description">{item.longDescription}</p>
                      <div className="features-list">
                        <h4>Key Features</h4>
                        <ul>
                          {item.features.map((feature, i) => (
                            <li key={i}>{feature}</li>
                          ))}
                        </ul>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </section>
  );
};

export default Projects;
